export interface AccountTreeNode {
  id?: number | string
  account?: string
  accountGroup?: string
  accountType?: string
  status?: number
  leaf?: boolean
  children?: AccountTreeNode[]
}

export interface AccountPickerTreeItem {
  title: string
  value: string | number
  key: string
  status?: number
  disabled?: boolean
  selectable?: boolean
  children?: AccountPickerTreeItem[]
}

const hasAccountChildren = (node?: AccountTreeNode | null) =>
  Array.isArray(node?.children) && node!.children!.length > 0

export const isAccountBranchNode = (node?: AccountTreeNode | null) => {
  if (!node) return false
  if (node.leaf === true) return false
  if (hasAccountChildren(node)) return true
  if (node.leaf === false) {
    const accountName = node.account && String(node.account).trim()
    return !accountName
  }
  return false
}

export const getAccountNodeTitle = (node?: AccountTreeNode | null) => {
  if (!node) return ''
  return node.account || node.accountGroup || ''
}

export const findAccountNodeById = (
  nodes: AccountTreeNode[] | undefined | null,
  id: string | number | null | undefined,
): AccountTreeNode | null => {
  if (id == null || !Array.isArray(nodes)) return null
  for (const node of nodes) {
    if (!node) continue
    if (node.id === id || String(node.id) === String(id)) return node
    const found = findAccountNodeById(node.children, id)
    if (found) return found
  }
  return null
}

export const findAccountNodeByTitle = (
  nodes: AccountTreeNode[] | undefined | null,
  title: string,
): AccountTreeNode | null => {
  const target = String(title || '').trim()
  if (!target || !Array.isArray(nodes)) return null
  const walk = (list: AccountTreeNode[]): AccountTreeNode | null => {
    for (const node of list) {
      if (!node) continue
      if (String(getAccountNodeTitle(node)).trim() === target) return node
      const found = walk(node.children || [])
      if (found) return found
    }
    return null
  }
  return walk(nodes)
}

export const findAccountByAccountAndGroup = (
  nodes: AccountTreeNode[] | undefined | null,
  account: string,
  accountGroup?: string | null,
): AccountTreeNode | null => {
  const targetAccount = String(account || '').trim()
  if (!targetAccount || !Array.isArray(nodes)) return null
  const targetGroup = accountGroup == null ? '' : String(accountGroup).trim()
  const matches: { node: AccountTreeNode; priority: number }[] = []
  const walk = (list: AccountTreeNode[]) => {
    for (const node of list) {
      if (!node) continue
      const nodeAccount = String(node.account || '').trim()
      const nodeGroup = String(node.accountGroup || '').trim()
      if (nodeAccount === targetAccount) {
        const isLeaf = !isAccountBranchNode(node)
        const groupMatches = targetGroup ? nodeGroup === targetGroup : nodeGroup === ''
        const priority = (isLeaf ? 2 : 0) + (groupMatches ? 1 : 0)
        matches.push({ node, priority })
      }
      walk(node.children || [])
    }
  }
  walk(nodes)
  if (!matches.length) return null
  matches.sort((a, b) => b.priority - a.priority)
  return matches[0]!.node
}

export const filterAccountTreeByAccountType = (
  nodes: AccountTreeNode[] | undefined | null,
  accountType?: string,
): AccountTreeNode[] => {
  if (!accountType || !Array.isArray(nodes)) return nodes || []
  const target = String(accountType).trim()
  if (!target) return nodes
  const walk = (list: AccountTreeNode[]): AccountTreeNode[] =>
    list.reduce<AccountTreeNode[]>((acc, node) => {
      if (!node) return acc
      if (isAccountBranchNode(node)) {
        const filteredChildren = walk(node.children || [])
        if (filteredChildren.length > 0) {
          acc.push({ ...node, children: filteredChildren })
        }
        return acc
      }
      const nodeType = node.accountType && String(node.accountType).trim()
      if (nodeType === target) acc.push(node)
      return acc
    }, [])
  return walk(nodes)
}

export const buildAccountGroupTree = (
  nodes: AccountTreeNode[] | undefined | null,
  excludeIds: Array<string | number> = [],
  options?: { leafOnly?: boolean; disableInactive?: boolean },
): AccountPickerTreeItem[] => {
  if (!Array.isArray(nodes)) return []
  const { leafOnly = true, disableInactive = true } = options || {}
  const excludeSet = new Set((excludeIds || []).map((id) => String(id)))

  const hasSelectableLeaf = (item: AccountPickerTreeItem): boolean => {
    const hasChildren = item.children && item.children.length > 0
    if (!hasChildren) return !item.disabled
    return item.children!.some(hasSelectableLeaf)
  }

  const decorate = (list: AccountPickerTreeItem[]): AccountPickerTreeItem[] =>
    list.map((item) => {
      const hasChildren = item.children && item.children.length > 0
      const nextItem = { ...item }
      if (leafOnly && hasChildren) {
        nextItem.selectable = false
      }
      if (hasChildren) {
        nextItem.children = decorate(item.children!)
        if (leafOnly && disableInactive && !hasSelectableLeaf(nextItem)) {
          nextItem.disabled = true
        }
      }
      return nextItem
    })

  const walk = (list: AccountTreeNode[]): AccountPickerTreeItem[] =>
    list.reduce<AccountPickerTreeItem[]>((acc, node) => {
      if (!node || node.id == null) return acc
      if (excludeSet.has(String(node.id))) return acc
      const title = getAccountNodeTitle(node)
      if (!title) return acc
      const item: AccountPickerTreeItem = {
        title,
        value: node.id,
        key: String(node.id),
        status: node.status,
        disabled: disableInactive && Number(node.status) !== 1,
      }
      const children = walk(node.children || [])
      if (children.length > 0) item.children = children
      return acc.concat(item)
    }, [])

  return decorate(walk(nodes))
}

const isAccountTreeList = (list: AccountTreeNode[]) => {
  if (!Array.isArray(list) || list.length === 0) return false
  return list.some(
    (item) =>
      item &&
      (isAccountBranchNode(item) || item.leaf === true || Array.isArray(item.children)),
  )
}

export const normalizeAccountListResponse = (data: any): AccountTreeNode[] => {
  if (!data) return []
  if (Array.isArray(data)) return data
  const rawItems = data.items
  if (!Array.isArray(rawItems)) return []
  if (isAccountTreeList(rawItems)) return rawItems
  return rawItems
}

export interface AccountTreeRow {
  key: string
  title: string
  depth: number
  isBranch: boolean
  disabled: boolean
  selectable: boolean
  nodeId: string | number
  rawNode: AccountTreeNode
}

const hasMatchingDescendant = (item: AccountPickerTreeItem, q: string): boolean => {
  if (item.title.toLowerCase().includes(q)) return true
  if (!item.children?.length) return false
  return item.children.some((child) => hasMatchingDescendant(child, q))
}

export const flattenVisibleAccountTree = (
  tree: AccountPickerTreeItem[],
  expanded: Set<string>,
  rawNodes: AccountTreeNode[],
  search = '',
): AccountTreeRow[] => {
  const q = search.trim().toLowerCase()
  const rows: AccountTreeRow[] = []

  const walk = (items: AccountPickerTreeItem[], depth: number, ancestorsExpanded = true) => {
    for (const item of items) {
      const hasChildren = !!(item.children && item.children.length)
      const rawNode = findAccountNodeById(rawNodes, item.value)
      if (!rawNode) continue
      const titleMatches = !q || item.title.toLowerCase().includes(q)

      if (hasChildren) {
        if (q) {
          if (!hasMatchingDescendant(item, q)) continue
        } else if (!ancestorsExpanded) {
          continue
        }
        rows.push({
          key: item.key,
          title: item.title,
          depth,
          isBranch: true,
          disabled: !!item.disabled,
          selectable: false,
          nodeId: item.value,
          rawNode,
        })
        const isExpanded = !!q || expanded.has(item.key)
        walk(item.children!, depth + 1, isExpanded)
        continue
      }

      if (!titleMatches) continue
      if (!ancestorsExpanded && !q) continue
      rows.push({
        key: item.key,
        title: item.title,
        depth,
        isBranch: false,
        disabled: !!item.disabled,
        selectable: item.selectable !== false && !item.disabled,
        nodeId: item.value,
        rawNode,
      })
    }
  }

  walk(tree, 0, true)
  return rows
}

export const getAccountAncestorIds = (
  nodes: AccountTreeNode[] | undefined | null,
  targetId: string | number | null | undefined,
): Array<string | number> => {
  if (targetId == null || targetId === '' || !Array.isArray(nodes)) return []
  const target = String(targetId)
  const walk = (
    list: AccountTreeNode[],
    trail: Array<string | number>,
  ): Array<string | number> | null => {
    for (const node of list) {
      if (!node || node.id == null) continue
      if (String(node.id) === target) return trail
      const found = walk(node.children || [], trail.concat(node.id))
      if (found) return found
    }
    return null
  }
  return walk(nodes, []) || []
}

export const getAccountHighlightIds = (
  nodes: AccountTreeNode[],
  selectedId: string | number | null | undefined,
): Set<string> => {
  if (selectedId == null || selectedId === '') return new Set()
  const ancestors = getAccountAncestorIds(nodes, selectedId)
  return new Set([...ancestors, selectedId].map((id) => String(id)))
}

export const resolveAccountSelection = (
  nodes: AccountTreeNode[],
  account?: string,
  accountGroup?: string,
  accountId?: string | number | null,
) => {
  if (accountId != null && accountId !== '') {
    const byId = findAccountNodeById(nodes, accountId)
    if (byId) return byId
  }
  if (!account) return null
  return (
    findAccountByAccountAndGroup(nodes, account, accountGroup) ||
    findAccountNodeByTitle(nodes, account)
  )
}
